/**
 * VehicleMakesPage — the makes present in the fleet (#2201).
 *
 * Not a registry: the list is derived from `vehicles.make`, so an exotic car
 * appears here the moment it is entered and leaves when the last one does.
 * A make can be given settings — a manufacturer service book and the model
 * years it covers, service intervals, a default parts supplier — and those are
 * the only writable part of this page.
 *
 * Search is client-side and matches models too, so looking for "Corolla" finds
 * Toyota. The endpoint accepts `q`, but the list is one row per make and a
 * workshop has tens of makes, not thousands — filtering in the browser avoids a
 * round trip per keystroke.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useVehicleMakes, type VehicleMake } from '@/hooks/useVehicles';
import { useClientPagination } from '@/hooks/usePagedQuery';
import { useAuthStore } from '@/stores/authStore';
import { VehicleMakeSettingsDialog } from './VehicleMakeSettingsDialog';
import {
  DataTable,
  type DataTableColumn,
  EntityListResults,
  ListPageHeader,
  SearchField,
  VehicleMakeLogo,
} from '@/components/common';

/** Models listed inline before the rest becomes a count. */
const MODELS_SHOWN = 4;

function compareMakes(a: VehicleMake, b: VehicleMake, sortKey: string): number {
  switch (sortKey) {
    case 'make':
      return a.make.localeCompare(b.make, 'fi');
    case 'vehicleCount':
      return a.vehicleCount - b.vehicleCount;
    case 'modelCount':
      return a.models.length - b.models.length;
    default:
      return 0;
  }
}

/** "2019–2026", "2019–" or "—" when no model year is recorded. */
function formatYearRange(from: number | null, to: number | null): string {
  if (from === null && to === null) return '—';
  if (from !== null && to !== null) return from === to ? String(from) : `${from}–${to}`;
  return from !== null ? `${from}–` : `–${to ?? ''}`;
}

export default function VehicleMakesPage() {
  const { t } = useTranslation('vehicles');
  // Without the permission the list is read-only: rows do not open (#2201).
  const canManage = useAuthStore((state) => state.hasPermission)('vehicles.update');
  const [search, setSearch] = useState('');
  // Held by make name, not by object: the list refetches after a save, and the
  // old object would leave the dialog showing stale settings.
  const [editing, setEditing] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useVehicleMakes();

  const makes = useMemo(() => {
    const all = data ?? [];
    const term = search.trim().toLocaleLowerCase('fi');
    if (term.length === 0) return all;
    // Matches the model too: someone looking for "Corolla" means Toyota.
    return all.filter(
      (make) =>
        make.make.toLocaleLowerCase('fi').includes(term) ||
        make.models.some((model) => model.model.toLocaleLowerCase('fi').includes(term)),
    );
  }, [data, search]);

  // The whole list is loaded, so it pages in the browser (#2744). Sort before
  // slicing: sorting inside DataTable would only reorder the page on screen.
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'make',
    direction: 'asc',
  });
  const sortedMakes = useMemo(() => {
    const sorted = [...makes].sort((a, b) => compareMakes(a, b, sort.key));
    return sort.direction === 'asc' ? sorted : sorted.reverse();
  }, [makes, sort]);
  const { pageRows, pagination } = useClientPagination(sortedMakes);
  const { onPageChange } = pagination;
  // A new search or sort starts from the first page.
  useEffect(() => {
    onPageChange(0);
  }, [search, sort, onPageChange]);

  // Resolved from the current list, so a save is reflected without reopening.
  const editingMake = useMemo(
    () => (editing === null ? null : (data ?? []).find((make) => make.make === editing) ?? null),
    [data, editing],
  );

  const columns: DataTableColumn<VehicleMake>[] = [
    {
      header: t('makes.table.make'),
      sortKey: 'make',
      renderCell: (make) => (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <VehicleMakeLogo make={make.make} size={28} />
          <Typography variant="body2" fontWeight={500}>
            {make.make}
          </Typography>
        </Stack>
      ),
    },
    {
      header: t('makes.table.vehicleCount'),
      sortKey: 'vehicleCount',
      align: 'right',
      renderCell: (make) => <Typography variant="body2">{make.vehicleCount}</Typography>,
    },
    {
      header: t('makes.table.models'),
      sortKey: 'modelCount',
      renderCell: (make) => {
        if (make.models.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        // Most common models first; the tail is a count, so a make with fifty
        // models does not push the other columns off the row.
        const shown = [...make.models]
          .sort((a, b) => b.vehicleCount - a.vehicleCount)
          .slice(0, MODELS_SHOWN);
        const rest = make.models.length - shown.length;
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {shown.map((model) => (
              <Tooltip
                key={model.model}
                title={t('makes.modelTooltip', {
                  count: model.vehicleCount,
                  years: formatYearRange(model.yearFrom, model.yearTo),
                })}
              >
                <Chip size="small" variant="outlined" label={model.model} />
              </Tooltip>
            ))}
            {rest > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                {t('makes.moreModels', { count: rest })}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      header: t('makes.table.settings'),
      renderCell: (make) =>
        make.settings === null ? (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {make.settings.serviceBookUrl !== null && (
              <Tooltip title={t('makes.hasServiceBook')}>
                <Chip
                  size="small"
                  icon={<MenuBookIcon />}
                  label={formatYearRange(
                    make.settings.serviceBookYearFrom,
                    make.settings.serviceBookYearTo,
                  )}
                />
              </Tooltip>
            )}
            {(make.settings.serviceIntervalKm !== null ||
              make.settings.serviceIntervalMonths !== null) && (
              <Chip size="small" icon={<SettingsIcon />} label={t('makes.hasInterval')} />
            )}
          </Stack>
        ),
    },
  ];

  const renderMobileCard = (make: VehicleMake) => (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <VehicleMakeLogo make={make.make} size={28} />
        <Typography variant="body2" fontWeight={600}>
          {make.make}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {t('makes.countsSummary', {
          vehicles: make.vehicleCount,
          models: make.models.length,
        })}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <ListPageHeader title={t('makes.title')} subtitle={t('makes.subtitle')} />

      <SearchField value={search} onChange={setSearch} placeholder={t('makes.searchPlaceholder')} />

      <EntityListResults
        loading={isLoading}
        isError={isError}
        errorText={t('makes.errorLoading')}
        error={error}
        onRefetch={() => void refetch()}
        resultCountText={t('makes.searchResultCount', { count: makes.length })}
        pagination={pagination}
      >
        <DataTable<VehicleMake>
          columns={columns}
          data={pageRows}
          getRowKey={(make) => make.make}
          onRowClick={canManage ? (make) => { setEditing(make.make); } : undefined}
          renderMobileCard={renderMobileCard}
          loading={isLoading}
          emptyMessage={t('makes.empty')}
          sortKey={sort.key}
          sortDirection={sort.direction}
          onSortChange={(key, direction) => { setSort({ key, direction }); }}
          ariaLabel={t('makes.title')}
        />
      </EntityListResults>

      <VehicleMakeSettingsDialog
        open={editingMake !== null}
        make={editingMake}
        onClose={() => { setEditing(null); }}
      />
    </Box>
  );
}
